import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import {
  technologicalTransferDomains,
  technologicalTransferPartnerTypes,
  technologicalTransferResults,
  technologicalTransferTypes,
} from '@/module/Author/TechnologicalTransfer';

import { authorSchema, publishedAtSchema } from '@/core/components';

export const technologicalTransferSchema = z
  .object({
    title: z.string().min(3),
    registrationNo: z.string().min(3),
    contractNo: z.string().min(3).nullish(),
    partnershipAgreement: z.string().min(3),
    protocol: z.string().min(3),
    type: z.enum(technologicalTransferTypes),
    typeName: z.string().min(3).nullable(),
    partnerType: z.enum(technologicalTransferPartnerTypes),
    partnerTypeName: z.string().min(3).nullable(),
    domain: z.enum(technologicalTransferDomains),
    domainName: z.string().min(3).nullable(),
    results: z.array(z.enum(technologicalTransferResults)).refine(value => value.length > 0, {
      params: { i18n: 'custom.required' },
    }),
    resultName: z.string().min(3).nullable(),
    startDate: publishedAtSchema,
    endDate: publishedAtSchema,
    authors: z.array(authorSchema).min(1),
  })
  .transform(data => {
    return {
      ...data,
      typeName: data.type === 'OTHER' ? data.typeName : null,
      partnerTypeName: data.partnerType === 'OTHER' ? data.partnerTypeName : null,
      domainName: data.domain === 'OTHER' ? data.domainName : null,
      resultName: data.results.includes('R0_OTHER') ? data.resultName : null,
    };
  })
  .refine(data => data.type !== 'OTHER' || data.typeName !== null, {
    path: ['typeName'],
    params: { i18n: 'custom.required' },
  })
  .refine(data => data.partnerType !== 'OTHER' || data.partnerTypeName !== null, {
    path: ['partnerTypeName'],
    params: { i18n: 'custom.required' },
  })
  .refine(data => data.domain !== 'OTHER' || data.domainName !== null, {
    path: ['domainName'],
    params: { i18n: 'custom.required' },
  })
  .refine(data => !data.results.includes('R0_OTHER') || data.resultName !== null, {
    path: ['resultName'],
    params: { i18n: 'custom.required' },
  });

export const defaultTechnologicalTransferValues: DefaultValues<TechnologicalTransferSchema> = {
  title: '',
  registrationNo: '',
  typeName: null,
  partnerTypeName: null,
  domainName: null,
  results: [],
  resultName: null,
};

export type TechnologicalTransferSchema = z.infer<typeof technologicalTransferSchema>;
